// The Custom Notification editor's fields and buttons, shared by the header's
// Custom Notifications pop-up (`RemindersScreen`) and an Admin's editor on
// Assignments → Notifications (owner, 2026-11-02 — D-454), so both ask the same
// questions in the same order. It holds no state: the screen owns the form, the
// toggle and the save. `lead` is drawn above the Type — the Admin's Assigned To field.
import type { ReactNode } from 'react'
import {
  Button,
  Checkbox,
  Dropdown,
  Field,
  FieldContextProvider,
  Input,
  MessageBar,
  MessageBarBody,
  Option,
  Spinner,
  Text,
  ToggleButton,
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
  useFieldContext_unstable,
} from '@fluentui/react-components'
import {
  DAY_TOKENS,
  KIND_OPTIONS,
  searchTimeOptions,
  timeLabel,
  type NotificationKind,
  type ReminderForm,
  type StepOption,
} from '../lib/reminders'
import { EntityPicker } from './EntityPicker'
import { StatusToggle } from './StatusToggle'

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: '14px', minWidth: 0 },
  daysRow: { display: 'flex', flexWrap: 'wrap', gap: '4px' },
  // ToggleButton's own "checked" look is a near-invisible neutral tint
  // (`colorNeutralBackground1Selected`, same family as the unchecked
  // background) — selected vs. not is unmistakable only once this wins.
  // Passed last into `mergeClasses` (never a template join, per
  // app/README.md's Fluent UI v9 gotchas), so Griffel's own property-level
  // merge lets it override the built-in checked/hover rules deterministically
  // rather than racing them on CSS specificity. `shorthands.borderColor`
  // (not a plain `borderColor` key) so the override lands on the same
  // per-side properties Fluent's own checked style sets.
  dayChecked: {
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    ...shorthands.borderColor(tokens.colorBrandBackground),
    ':hover': {
      backgroundColor: tokens.colorBrandBackgroundHover,
      color: tokens.colorNeutralForegroundOnBrand,
      ...shorthands.borderColor(tokens.colorBrandBackgroundHover),
    },
    ':active': {
      backgroundColor: tokens.colorBrandBackgroundPressed,
      color: tokens.colorNeutralForegroundOnBrand,
      ...shorthands.borderColor(tokens.colorBrandBackgroundPressed),
    },
  },
  nameInput: { width: '100%', minWidth: 0 },
  checks: { display: 'flex', flexDirection: 'column', gap: '2px' },
  // The Active/Inactive toggle at the left, Cancel and Save at the right, as on
  // every References and Rules editor (D-201).
  formActions: { display: 'flex', gap: '8px', alignItems: 'center', marginTop: '4px' },
  formActionsEnd: { display: 'flex', gap: '8px', marginLeft: 'auto' },
  empty: { color: tokens.colorNeutralForeground3, padding: '8px 0' },
})

/**
 * EACH STEP CHECKBOX IS NAMED BY ITS OWN STEP (QA 2026-09-30, T-01). A Fluent
 * `Field` hands its control id, its label and its hint to EVERY control inside
 * it, so all eight checkboxes shared one id, and the Field's label plus each
 * checkbox's own label pointed at the first of them: a screen reader heard
 * "Reminder Steps" eight times. The checkboxes now sit outside the Field's
 * control context, each with its own id and label, and the group carries the
 * Field's label and hint instead. The Field itself, its required marker and
 * its hint line are unchanged (D-234, D-236).
 */
function StepCheckGroup({ className, children }: { className: string; children: ReactNode }) {
  const field = useFieldContext_unstable()
  return (
    <div className={className} role="group" aria-labelledby={field?.labelId} aria-describedby={field?.hintId}>
      <FieldContextProvider value={undefined}>{children}</FieldContextProvider>
    </div>
  )
}

export function CustomNotificationForm({
  form,
  onChange,
  stepOptions,
  noSteps,
  triggeredLabel,
  dialogHost,
  editing,
  active,
  onActiveChange,
  saving,
  error,
  onCancel,
  onSave,
  lead,
  extraActions,
}: {
  form: ReminderForm
  onChange: (form: ReminderForm) => void
  stepOptions: readonly StepOption[]
  /** Said in place of the checkboxes when there is no step to offer. */
  noSteps: string
  /** The Triggered question over the steps: "Notify me when an invoice arrives at". */
  triggeredLabel: string
  /** The pop-up the pickers open inside (app/README.md's Fluent UI v9 gotcha). */
  dialogHost: HTMLElement | null
  /** On edit, the Active/Inactive toggle shows; a new notification is Active (D-201). */
  editing: boolean
  active: boolean
  onActiveChange: (active: boolean) => void
  saving: boolean
  error: string | null
  onCancel: () => void
  onSave: () => void
  lead?: ReactNode
  /** Beside Cancel and Save — the Admin editor's Remove. */
  extraActions?: ReactNode
}) {
  const s = useStyles()
  const toggle = <T,>(list: T[], v: T): T[] => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v])

  // TITLE-CASE LABELS (D-236). The
  // "(optional)" markers went with the move to titles; the four fields
  // `validateForm` insists on carry the required marker instead, so which
  // ones may be left blank is still on screen.
  const stepChecks =
    stepOptions.length === 0 ? (
      <Text className={s.empty}>{noSteps}</Text>
    ) : (
      <StepCheckGroup className={s.checks}>
        {stepOptions.map((o) => (
          <Checkbox
            key={o.key}
            label={o.label}
            checked={form.steps.includes(o.key)}
            onChange={() => onChange({ ...form, steps: toggle(form.steps, o.key) })}
          />
        ))}
      </StepCheckGroup>
    )
  const kindLabel = KIND_OPTIONS.find((o) => o.value === form.kind)?.label ?? ''
  return (
    <div className={s.root}>
      {lead}

      {/* THE TYPE FIRST (owner, 2026-10-21 — D-391): Scheduled sends a digest
          on its days at its time; Triggered sends an email when an invoice
          arrives at one of its steps. Nothing under it shows until it is chosen. */}
      <Field label="Type" required>
        <Dropdown
          placeholder="Choose a type"
          mountNode={dialogHost ?? undefined}
          selectedOptions={form.kind === '' ? [] : [form.kind]}
          value={kindLabel}
          onOptionSelect={(_, d) => onChange({ ...form, kind: (d.optionValue as NotificationKind | undefined) ?? '' })}
        >
          {KIND_OPTIONS.map((o) => (
            <Option key={o.value} value={o.value} text={o.label}>
              {o.label}
            </Option>
          ))}
        </Dropdown>
      </Field>

      {form.kind !== '' && (
        <Field label="Title">
          <Input
            className={s.nameInput}
            value={form.title}
            placeholder="e.g. Morning approvals"
            onChange={(_, d) => onChange({ ...form, title: d.value })}
          />
        </Field>
      )}

      {form.kind === 'scheduled' && (
        <>
          <Field label="Days Of The Week" required>
            <div className={s.daysRow}>
              {DAY_TOKENS.map((day) => (
                <ToggleButton
                  key={day}
                  size="small"
                  checked={form.days.includes(day)}
                  className={mergeClasses(form.days.includes(day) && s.dayChecked)}
                  onClick={() => onChange({ ...form, days: toggle(form.days, day) })}
                >
                  {day}
                </ToggleButton>
              ))}
            </div>
          </Field>

          {/* QUARTER HOURS, SEARCHABLE (owner, 2026-10-14) — the digest goes out
              on a fifteen-minute tick, so these are the only times it can keep.
              Typing `830` or `8:30` finds 8:30 AM. */}
          <Field label="Time Of Day (Eastern)" required>
            <EntityPicker
              ariaLabel="Time Of Day (Eastern)"
              value={{ id: form.time, name: timeLabel(form.time) }}
              mountNode={dialogHost}
              search={(term) => Promise.resolve(searchTimeOptions(term).map((o) => ({ id: o.value, primary: o.label })))}
              onSelect={(o) => onChange({ ...form, time: o.id })}
            />
          </Field>

          {/* The required marker sits on the LABEL, not the Field: a Field's
              `required` reaches every Checkbox inside it, and each step then
              carried its own red asterisk (found on screen, 2026-09-30). */}
          <Field label={{ children: 'Steps', required: true }}>{stepChecks}</Field>
        </>
      )}

      {/* A TRIGGERED NOTIFICATION HAS NO SCHEDULE (D-391): the days, the time
          and the Steps label give way to one question over the same steps —
          those where this person holds an Invoices role. */}
      {form.kind === 'triggered' && <Field label={{ children: triggeredLabel, required: true }}>{stepChecks}</Field>}

      {error && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      <div className={s.formActions}>
        {/* ON EDIT ONLY, as on every editor: a new notification is Active (D-201). */}
        {editing && <StatusToggle value={active} onChange={onActiveChange} disabled={saving} />}
        <div className={s.formActionsEnd}>
          {extraActions}
          <Button appearance="secondary" disabled={saving} onClick={onCancel}>
            Cancel
          </Button>
          <Button appearance="primary" disabled={saving} icon={saving ? <Spinner size="tiny" /> : undefined} onClick={onSave}>
            Save
          </Button>
        </div>
      </div>
    </div>
  )
}
